import React, { createContext, useContext, useState, useEffect } from 'react'
import { useAuth } from './AuthContext'
import { getBranding } from '../services/api'
import { ACAD_COLORS } from '../constants/colors'
import type { BrandingConfig } from '../types'

interface BrandingContextType {
  branding: BrandingConfig | null
  loading: boolean
}

const BrandingContext = createContext<BrandingContextType | null>(null)

/** Default branding applied when no institution-specific branding is loaded */
const DEFAULT_BRANDING: Partial<BrandingConfig> = {
  primary_color: ACAD_COLORS.primary,
  secondary_color: ACAD_COLORS.secondary,
  accent_color: ACAD_COLORS.accent,
  institution_name: 'Academic Discovery Portal',
}

function applyBrandingCssProperties(branding: Partial<BrandingConfig>) {
  const root = document.documentElement
  root.style.setProperty('--brand-primary', branding.primary_color || ACAD_COLORS.primary)
  root.style.setProperty('--brand-secondary', branding.secondary_color || ACAD_COLORS.secondary)
  root.style.setProperty('--brand-accent', branding.accent_color || ACAD_COLORS.accent)
}

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth()
  const [branding, setBranding] = useState<BrandingConfig | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!isAuthenticated) {
      // Apply defaults when not authenticated
      applyBrandingCssProperties(DEFAULT_BRANDING)
      setBranding(null)
      return
    }

    let cancelled = false

    async function fetchBranding() {
      setLoading(true)
      try {
        const config = await getBranding()
        if (cancelled) return

        if (config) {
          setBranding(config)
          applyBrandingCssProperties(config)
        } else {
          applyBrandingCssProperties(DEFAULT_BRANDING)
        }
      } catch {
        if (!cancelled) {
          applyBrandingCssProperties(DEFAULT_BRANDING)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchBranding()

    return () => {
      cancelled = true
    }
  }, [isAuthenticated])

  const value: BrandingContextType = {
    branding,
    loading,
  }

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>
}

export function useBranding(): BrandingContextType {
  const context = useContext(BrandingContext)
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider')
  }
  return context
}
